import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Mail, Pencil, Plus } from 'lucide-react';
import { composeLink } from '@/lib/email';
import { supabase } from '@/lib/supabase';
import { currentMembership, useCanEdit, type Membership } from '@/lib/queries';
import { ageBandLabel, date, gradeLabel } from '@/lib/format';
import { adminLinks } from '@/lib/links';
import { History } from '@/components/admin/History';
import { ErrorNote, Spinner } from '@/components/Layout';
import { AddDojo, DojoInstructors, EditDojo, INSTRUCTOR_COLUMNS, REGIONS, type DojoRecord, type InstructorRecord } from '@/components/admin/Instructors';

const today = () => new Date().toISOString().slice(0, 10);
const isCurrent = (ms: Membership[]) => { const m = currentMembership({ memberships: ms } as never); return !!m && m.status === 'active' && m.ends_on >= today(); };

/** All dojos with member counts; each links to its page. */
export function AdminDojos() {
  const canEdit = useCanEdit();
  const [adding, setAdding] = useState(false);
  const q = useQuery({
    queryKey: ['admin-dojos'],
    queryFn: async () => {
      const { data, error } = await supabase.from('dojo').select('id,name,short_name,region,town,status,people:person(id,status,memberships:membership(ends_on,status))').order('short_name');
      if (error) throw error;
      return data as unknown as { id: string; name: string; short_name: string | null; region: string; town: string | null; status: string; people: { id: string; status: string; memberships: Membership[] }[] }[];
    },
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNote>Could not load dojos.</ErrorNote>;
  return (
    <div className="space-y-4">
    {canEdit && <div className="flex justify-end"><button className="btn-ghost border border-stone-300" onClick={() => setAdding(!adding)}><Plus size={16} /> Add a dojo</button></div>}
    {adding && <AddDojo onCancel={() => setAdding(false)} />}
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-stone-200 text-ink-500"><tr><th className="px-5 py-3 font-medium">Dojo</th><th className="pr-3 font-medium">Region</th><th className="pr-3 font-medium">People</th><th className="pr-5 font-medium">Current members</th></tr></thead>
        <tbody className="divide-y divide-stone-100">
          {q.data?.map(d => {
            const people = d.people.filter(p => p.status === 'active');
            return (
              <tr key={d.id}>
                <td className="px-5 py-2.5"><Link to={adminLinks.dojo(d.id)} className="font-medium link">{d.short_name ?? d.name}</Link><span className="block text-xs text-ink-500">{d.name}{d.town && `, ${d.town}`}</span></td>
                <td className="pr-3">{REGIONS[d.region] ?? d.region}</td>
                <td className="pr-3">{people.length}</td>
                <td className="pr-5">{people.filter(p => isCurrent(p.memberships)).length}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
}

/** One dojo: details, instructors (with DBS and insurance dates), and everyone who trains there. */
export function AdminDojo() {
  const canEdit = useCanEdit();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-dojo', id] });
  const q = useQuery({
    queryKey: ['admin-dojo', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('dojo').select(`id,name,short_name,region,town,venue_address,postcode,website,is_honbu,status,
        instructors:dojo_instructor(${INSTRUCTOR_COLUMNS}),
        people:person(id,household_id,first_name,last_name,grade_rank,date_of_birth,status,household:household_id(name),memberships:membership(id,starts_on,ends_on,status,plan:plan_id(name)))`).eq('id', id).single();
      if (error) throw error;
      return data as unknown as DojoRecord & {
        instructors: InstructorRecord[];
        people: { id: string; household_id: string; first_name: string; last_name: string; grade_rank: number | null; date_of_birth: string | null; status: string; household: { name: string } | null; memberships: Membership[] }[];
      };
    },
  });
  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <ErrorNote>Could not load this dojo.</ErrorNote>;
  const d = q.data;
  const people = d.people.filter(p => p.status === 'active').sort((a, b) => a.last_name.localeCompare(b.last_name));
  return (
    <div className="space-y-6">
      <Link to="/admin/dojos" className="text-sm text-ink-500 hover:underline">← All dojos</Link>
      {editing ? <EditDojo d={d} onDone={async saved => { setEditing(false); if (saved) await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['admin-dojos'] }), qc.invalidateQueries({ queryKey: ['dojos'] })]); }} /> : (
        <section className="card flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">{d.name}{d.is_honbu && <span className="pill ml-2 bg-brand-100 text-brand-800">Honbu</span>}</h2>
            <p className="text-sm text-ink-500">{REGIONS[d.region] ?? d.region}{d.town && ` · ${d.town}`}{d.status !== 'active' && ' · closed'}</p>
            {d.venue_address && <p className="mt-1 text-sm">{d.venue_address}{d.postcode && `, ${d.postcode}`}</p>}
            {d.website && <a href={d.website} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm link">Website <ExternalLink size={12} /></a>}
          </div>
          <span className="flex flex-wrap gap-2">
            {canEdit && <Link {...composeLink({ kind: 'dojo', id: d.id })} className="btn-ghost border border-stone-300 text-sm"><Mail size={14} /> Email dojo</Link>}
            <Link to={adminLinks.instructorView(d.id)} className="btn-ghost border border-stone-300 text-sm">Open instructor view</Link>
            {canEdit && <button className="btn-ghost border border-stone-300 text-sm" onClick={() => setEditing(true)}><Pencil size={14} /> Edit dojo</button>}
          </span>
        </section>
      )}

      <DojoInstructors dojoId={d.id} instructors={d.instructors} onChanged={async () => { await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['admin-instructors'] })]); }} />

      <section className="card space-y-2">
        <h3 className="font-semibold">Members <span className="font-normal text-ink-500">({people.filter(p => isCurrent(p.memberships)).length} current of {people.length})</span></h3>
        {people.length ? (
          <table className="w-full text-left text-sm">
            <thead className="text-ink-500"><tr><th className="py-1 pr-3 font-medium">Name</th><th className="pr-3 font-medium">Grade</th><th className="pr-3 font-medium">Household</th><th className="font-medium">Membership</th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {people.map(p => {
                const m = currentMembership({ memberships: p.memberships } as never);
                return (
                  <tr key={p.id}>
                    <td className="py-2 pr-3"><Link to={adminLinks.person(p.id)} className="link">{p.first_name} {p.last_name}</Link>{ageBandLabel(p.date_of_birth) && <span className="text-ink-500"> · {ageBandLabel(p.date_of_birth)}</span>}</td>
                    <td className="pr-3">{gradeLabel(p.grade_rank)}</td>
                    <td className="pr-3"><Link to={adminLinks.household(p.household_id)} className="hover:underline">{p.household?.name}</Link></td>
                    <td>{isCurrent(p.memberships) ? `Until ${date(m!.ends_on)}` : m ? `Ended ${date(m.ends_on)}` : 'Not a member'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <p className="text-sm text-ink-500">Nobody has chosen this dojo yet.</p>}
      </section>
      <History by="dojo_id" id={d.id} />
    </div>
  );
}
